import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Pencil, Plus, Sparkles, CalendarDays, Tag, Check, User, ChevronLeft, ChevronRight,
} from 'lucide-react'
import { useMe } from '../lib/me.js'
import { canManage } from '../lib/role.js'
import { apiGet, cachedGet } from '../lib/api.js'
import { statusTone, STATUS_TONES } from '../data/patientStatuses.js'
import {
  asList, todayBKK, hm, codeCls, STATUS_TH,
  composeQueue, firstHuddle, goalCat, goalTarget, isHuddleBranchExcluded, groupAppts,
  planKey, planOutcome, groupSold, filledGoals, applyPatientStatus,
  huddleUserKey, readHuddleScope, writeHuddleScope,
} from '../lib/huddle.js'
import { resolveDefaultBranch } from '../lib/branch.js'
import HuddleEditor from '../components/huddle/HuddleEditor.jsx'
import StatusControl from '../components/StatusControl.jsx'
import ScaleTag, { ScaleLegend } from '../components/ScaleTag.jsx'
import PromotionPicker from '../components/promotion/PromotionPicker.jsx'
import { useHuddleStaff, assigneeNames } from '../lib/useHuddleStaff.js'
import { useXanoRealtime } from '../lib/realtime.js'
import {
  Page, PageHeader, Button, IconButton, Badge, FilterCard, Field, Input, Select, Tabs, Segmented,
  Loading, ErrorState, EmptyState, PatientLink, fmtDate, fmtDateText,
} from '../kit'

// ประชุมเช้า (Morning Huddle). Staff see the published huddle + today's live
// queue composed from /api/appointments. Managers additionally see drafts, can
// switch branch/date, and create/edit. Saving publishes to the branch at once
// — there is no draft step. Writes are gated at /api/huddle
// (the proxy 403s non-managers); the edit UI is hidden by canManage.
export default function Huddle() {
  const me = useMe()
  const manager = canManage(me)

  // The Bangkok day THIS VISIT belongs to, fixed at mount. The sticky filter
  // below is scoped to it, and a live todayBKK() that ticked over at midnight
  // mid-session would re-stamp yesterday's choice onto the new day instead of
  // letting it expire.
  const [today] = useState(todayBKK)
  const userKey = huddleUserKey(me)

  const [branchId, setBranchId] = useState('')
  const [branches, setBranches] = useState([])
  const [date, setDate] = useState(today)

  const [huddle, setHuddle] = useState(null)
  const [queue, setQueue] = useState(null)
  const [plans, setPlans] = useState([])     // huddle_upsell rows for the day
  const [sold, setSold] = useState([])       // promotion_patient rows for the day
  const [promoNames, setPromoNames] = useState(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(false)
  // Which (branch, day) the huddle currently in state was loaded FOR. The save
  // is a whole-row overwrite, so the editor must never be seeded from another
  // day's row while a load is in flight — `ready` below is that guarantee.
  const [loadedScope, setLoadedScope] = useState('')
  const reqId = useRef(0)

  // Where the page opens. Within the same day it reopens on whatever branch and
  // date were last looked at; only the first visit of a new day falls back to
  // the defaults — today, and the branch resolved server-side (today's timetable
  // branch → home branch → HQ remap (สนามเป้า→อารีย์)). Never empty.
  //
  // Nothing is fetched while branchId is '', so the restore lands before the
  // first load rather than after a wasted round trip to the default branch.
  useEffect(() => {
    if (!me || branchId !== '') return
    const saved = readHuddleScope(userKey, today)
    if (saved) { setDate(saved.date); setBranchId(saved.branchId); return }
    let alive = true
    resolveDefaultBranch(me).then((b) => { if (alive && b) setBranchId(String(b)) })
    return () => { alive = false }
  }, [me, branchId, userKey, today])

  // …and remember the pair for the rest of the day, wherever it was changed
  // from — the two pickers above or the editor's own scope switch.
  useEffect(() => {
    if (!me || branchId === '') return
    writeHuddleScope(userKey, today, branchId, date)
  }, [me, userKey, today, branchId, date])
  useEffect(() => {
    if (!manager) return
    cachedGet('/api/branches')
      .then((r) => setBranches(asList(r)
        .map((b) => ({ id: String(b.id ?? b.branch_id), name: b.name || b.branch_name || `#${b.id}` }))
        .filter((b) => !isHuddleBranchExcluded(b.name))))
      .catch(() => {})
  }, [manager])

  const branchName = branches.find((b) => b.id === String(branchId))?.name
    || (me && String(me.branch_id) === String(branchId) ? (me.branch_name || '') : '')

  const load = useCallback(async () => {
    if (branchId === '' || !date) return
    const rid = ++reqId.current
    setLoading(true); setError(null)
    try {
      // Staff only ever see a PUBLISHED huddle; managers see drafts too.
      const huddleParams = { branch_id: branchId, huddle_date: date, per_page: 1 }
      if (!manager) huddleParams.status = 'published'
      const [hRes, aRes, uRes] = await Promise.all([
        apiGet('/api/huddle', huddleParams),
        apiGet('/api/appointments', { type: 'dashboard', branch_id: branchId, start_date: date, end_date: date }).catch(() => []),
        apiGet('/api/huddle-upsell', { plan_date: date, branch_id: branchId }).catch(() => null),
      ])
      if (rid !== reqId.current) return
      setHuddle(firstHuddle(hRes))
      setQueue(composeQueue(aRes))
      setPlans(asList(uRes?.plans))
      setSold(asList(uRes?.sold))
      setLoadedScope(`${branchId}|${date}`)
    } catch (e) {
      if (rid !== reqId.current) return
      setError(e.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      if (rid === reqId.current) setLoading(false)
    }
  }, [branchId, date, manager])

  useEffect(() => { load() }, [load])

  // id → ชื่อโปรโมชั่น, so a plan row can show names instead of ids.
  useEffect(() => {
    cachedGet('/api/promotions')
      .then((r) => setPromoNames(new Map(asList(r).map((p) => [Number(p.id), p.Name || `#${p.id}`]))))
      .catch(() => {})
  }, [])

  // A status changed from inside the ต้องยืนยัน list — patch it in place rather
  // than refetching the whole day for one pill.
  const handlePatientStatus = useCallback((patientId, status) => {
    setQueue((q) => applyPatientStatus(q, patientId, status))
  }, [])

  // Live: another station changed a patient's status. Several people work the
  // ต้องยืนยัน list at 08:00 and each row carries an editable StatusControl, so a
  // status set at one counter was invisible at the next until someone reloaded.
  //
  // The `patient_update - patient` table trigger fires `recall_update` with
  // { type:"patient", action:"status", patient_id, status } on a real status
  // change. The event carries the NEW STATUS, so we patch that row in place and
  // refetch NOTHING — same "carry the delta, not a ping" rule the reimbursement
  // page follows, and it matters here because load() is three requests.
  //
  // The channel's other traffic is the recall lock (no `action` key), which this
  // page does not render — hence the action guard. There is no employee_id on a
  // status event (table 19 has no modified_by), so our own change comes back to
  // us; applyPatientStatus writes the value we already hold, so that is a no-op.
  useXanoRealtime('recall_update', (msg) => {
    const p = msg?.payload?.data ?? msg?.payload
    if (!p || typeof p !== 'object' || p.action !== 'status') return
    if (p.patient_id == null) return
    handlePatientStatus(p.patient_id, p.status || '')
  })

  const scope = `${branchId}|${date}`
  const ready = !loading && !error && loadedScope === scope
  const goals = filledGoals(huddle?.daily_goals)
  const notes = Array.isArray(huddle?.notes) ? huddle.notes.filter((n) => (n.body || '').trim()) : []
  const isDraft = huddle && huddle.status !== 'published'

  // Day stepping for the manager's date filter. ISO in, ISO out — calendar
  // arithmetic on the date itself, so the browser's timezone never enters.
  const shiftDay = (iso, n) => {
    const [y, m, d] = String(iso || today).split('-').map(Number)
    return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
  }
  const goDay = (next) => { setDate(next || todayBKK()); setEditing(false) }

  return (
    <Page>
      <PageHeader
        title={(
          <span className="inline-flex items-center gap-2 flex-wrap">
            ประชุมเช้า
            {/* Nothing writes a draft any more — this only ever marks a row
                saved before that changed, which staff still cannot see. */}
            {isDraft && manager && (
              <Badge tone="amber" title="บันทึกไว้เป็นฉบับร่างก่อนหน้านี้ — กด “แก้ไข” แล้วบันทึกอีกครั้งเพื่อให้ทีมเห็น">ทีมยังไม่เห็น</Badge>
            )}
          </span>
        )}
        subtitle={`${fmtDateText(date, { weekday: true })} · สาขา ${branchName || '—'}`}
        actions={manager && !editing && (
          // Disabled until the row on screen belongs to the branch+day in the
          // picker — opening mid-load would seed the editor from the PREVIOUS
          // day, and the full-save then overwrites this one.
          <Button
            icon={huddle ? Pencil : Plus}
            onClick={() => setEditing(true)}
            disabled={!ready}
            disabledReason={error ? 'โหลดไม่สำเร็จ' : 'กำลังโหลด…'}
          >
            {huddle ? 'แก้ไข' : 'สร้าง Daily Goal'}
          </Button>
        )}
      />

      {/* Managers pick the branch and the day; staff always see their own day. */}
      {manager && (
        <FilterCard compact>
          <Field label="วันที่">
            <div className="flex items-center gap-1.5">
              <IconButton icon={ChevronLeft} label="วันก่อนหน้า" variant="secondary" onClick={() => goDay(shiftDay(date, -1))} />
              <Input type="date" value={date} onChange={(e) => goDay(e.target.value)} className="!w-[160px]" />
              <IconButton icon={ChevronRight} label="วันถัดไป" variant="secondary" onClick={() => goDay(shiftDay(date, 1))} />
              <Button size="sm" variant="ghost" onClick={() => goDay(todayBKK())} disabled={date === todayBKK()}>วันนี้</Button>
            </div>
          </Field>
          {branches.length > 0 && (
            <Field label="สาขา" className="min-w-[180px]">
              <Select
                value={branchId}
                onChange={(v) => { setBranchId(String(v)); setEditing(false) }}
                options={branches.map((b) => ({ value: b.id, label: b.name }))}
              />
            </Field>
          )}
        </FilterCard>
      )}

      {loading && <Loading />}

      {!loading && error && <ErrorState message={error} onRetry={load} />}

      {/* Editor is a right-hand drawer (house pattern), so the day's huddle and
          live queue stay visible behind it. */}
      {editing && (
        <HuddleEditor
          initial={huddle}
          branchId={branchId}
          date={date}
          branchName={branchName}
          branches={branches}
          ready={ready}
          scopeError={error}
          onRetry={load}
          onScopeChange={(b, d) => { setBranchId(String(b)); setDate(d || todayBKK()) }}
          onCancel={() => setEditing(false)}
          onSaved={(saved) => { setEditing(false); if (saved && saved.id) setHuddle(saved); else load() }}
        />
      )}

      {!loading && !error && (
        <div className="space-y-5">
          {/* Manager-authored content */}
          {huddle ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-5">
              {/* Daily Goals */}
              {goals.length > 0 && (
                <section>
                  <h2 className="text-[13.5px] font-semibold text-slate-800 mb-2.5">🎯 Daily Goal</h2>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {goals.map((g, i) => {
                      const c = goalCat(g.category)
                      return (
                        <div key={i} className="flex items-start gap-2.5 rounded-xl bg-slate-50 ring-1 ring-slate-100 px-3 py-2.5">
                          <span className="text-[17px] leading-none mt-0.5">{c.icon}</span>
                          {/* Detail is optional — some categories say it all on
                              their own (No Zero Case), and a number needs no
                              prose. With no detail the category name IS the
                              goal, so it moves up to the main line rather than
                              staying a faint label over an empty one. */}
                          <div className="min-w-0 flex-1">
                            {(g.text || '').trim() ? (
                              <>
                                <div className="text-[11.5px] font-medium uppercase tracking-wide text-slate-400">{c.label}</div>
                                <div className="text-[13.5px] text-slate-800 leading-snug whitespace-pre-wrap">{g.text}</div>
                              </>
                            ) : (
                              <div className="text-[13.5px] text-slate-800 leading-snug">{c.label}</div>
                            )}
                          </div>
                          {g.target && <span className="flex-none self-start mt-0.5 text-[12.5px] font-semibold text-brand-700 bg-brand-50 rounded-md px-2 py-0.5 tabular-nums">{goalTarget(g.target)}</span>}
                        </div>
                      )
                    })}
                  </div>
                </section>
              )}

              {/* Announcements */}
              {(huddle.announcements || '').trim() && (
                <section>
                  <h2 className="text-[13.5px] font-semibold text-slate-800 mb-2">📢 ประกาศ</h2>
                  <div className="text-[13.5px] text-slate-700 leading-relaxed whitespace-pre-wrap">{huddle.announcements.trim()}</div>
                </section>
              )}

              {/* Notes */}
              {notes.length > 0 && (
                <section>
                  <h2 className="text-[13.5px] font-semibold text-slate-800 mb-2">📝 บันทึกตามหัวข้อ</h2>
                  <div className="space-y-2.5">
                    {notes.map((n, i) => (
                      <div key={i} className="border-l-2 border-brand-200 pl-3">
                        {(n.topic || '').trim() && <div className="text-[12.5px] font-semibold text-slate-700">{n.topic}</div>}
                        <div className="text-[13.5px] text-slate-700 leading-relaxed whitespace-pre-wrap">{n.body}</div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {goals.length === 0 && !(huddle.announcements || '').trim() && notes.length === 0 && (
                <div className="text-[13px] text-slate-400">Daily Goal นี้ยังไม่มีเนื้อหา</div>
              )}
            </div>
          ) : (
            <EmptyState
              icon={CalendarDays}
              title="ยังไม่มี Daily Goal สำหรับวันนี้"
              hint={manager ? 'กด “สร้าง Daily Goal” เพื่อกำหนดเป้าหมายและประกาศให้ทีม' : 'รอผู้จัดการเผยแพร่ Daily Goal ของวันนี้'}
            />
          )}

          {/* Live composed sections — always available from the appointment feed */}
          {queue && (
            <LiveSections
              queue={queue}
              plans={plans}
              sold={sold}
              promoNames={promoNames}
              planDate={date}
              branchId={branchId}
              onPlansChanged={setPlans}
              onPatientStatus={handlePatientStatus}
            />
          )}
        </div>
      )}
    </Page>
  )
}

/* -------------------------------------------------------------------------- */
/* Composed-at-read-time sections (not stored on the huddle row).              */
/* -------------------------------------------------------------------------- */
function LiveSections({ queue, plans, sold, promoNames, planDate, branchId, onPlansChanged, onPatientStatus }) {
  // Names for แผนอัปเซล's ผู้รับผิดชอบ — the roster this branch runs that day.
  const staffNames = useHuddleStaff(branchId, planDate)
  const { confirmedAppts, toConfirmAppts, notable } = queue
  const [tab, setTab] = useState('queue')
  const [mode, setMode] = useState('type')
  const [picker, setPicker] = useState(null) // { patientId, patientName, appointmentId, initial }

  // Plans keyed by appointment, and the day's real sales bucketed by patient —
  // together they give each row its planned count and whether it closed.
  const planFor = useMemo(() => {
    const m = new Map()
    for (const pl of plans) m.set(planKey(pl.appointment_id, pl.patient_id), pl)
    return m
  }, [plans])
  const soldIndex = useMemo(() => groupSold(sold), [sold])
  // Plans store only patient_id; the day's queue is where the names live.
  const nameByPatient = useMemo(
    () => new Map(queue.appts.map((a) => [a.patientId, a.patient])),
    [queue],
  )

  // ต้องยืนยัน is the calling list: whoever works it needs to set the patient's
  // status as they go, and to see whether a next booking already exists before
  // promising one. คิวนัดทั้งหมด stays read-only.
  const rows = tab === 'queue' ? confirmedAppts : toConfirmAppts
  const groups = useMemo(() => groupAppts(rows, mode), [rows, mode])

  const TABS = [
    { key: 'queue', label: 'คิวนัดทั้งหมด', badge: confirmedAppts.length },
    { key: 'confirm', label: 'ต้องยืนยัน', badge: toConfirmAppts.length },
    { key: 'upsale', label: 'เคสน่าสนใจ / Upsale', badge: notable.length + plans.length },
  ]

  // Upsert the returned plan into the day's list (empty plan = cleared).
  function onSaved(plan) {
    setPicker(null)
    if (!plan || !plan.id) return
    const kept = plans.filter((p) => p.id !== plan.id)
    onPlansChanged((plan.promotion_id || []).length && plan.active !== false ? [...kept, plan] : kept)
  }

  // NOT overflow-hidden below: the ต้องยืนยัน rows carry a StatusControl whose
  // dropdown is absolutely positioned, and any clipping ancestor cuts it off for
  // rows near the bottom of the list. Nothing here paints to the rounded corner
  // (a group header is never the last child), so clipping buys nothing.
  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      {/* Tabs */}
      <Tabs className="px-3" items={TABS} value={tab} onChange={setTab} />

      {/* Group toggle (queue + confirm) */}
      {tab !== 'upsale' && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100">
          <span className="text-[12px] text-slate-400">จัดกลุ่ม</span>
          <Segmented
            size="sm"
            value={mode}
            onChange={setMode}
            options={[{ value: 'type', label: 'ตามประเภท' }, { value: 'doctor', label: 'ตามหมอ' }]}
          />
          <span className="ml-auto flex items-center gap-3">
            <ScaleLegend className="hidden lg:inline-flex" />
            <span className="text-[11px] text-slate-400 hidden sm:block">
              {tab === 'queue' ? 'เฉพาะที่ยืนยันแล้ว (C/C*)' : 'ทุกนัดที่ยังไม่ยืนยัน'}
            </span>
          </span>
        </div>
      )}

      {/* Content */}
      {tab === 'upsale'
        ? <UpsaleTab notable={notable} plans={plans} soldIndex={soldIndex} promoNames={promoNames} staffNames={staffNames} nameByPatient={nameByPatient} />
        : (
          <GroupedQueue
            groups={groups}
            planFor={planFor}
            soldIndex={soldIndex}
            promoNames={promoNames}
            staffNames={staffNames}
            confirmMode={tab === 'confirm'}
            onPatientStatus={onPatientStatus}
            onPlanUpsell={(a) => setPicker({
              patientId    : a.patientId,
              patientName  : a.patient,
              appointmentId: a.id,
              initial      : planFor.get(planKey(a.id, a.patientId))?.promotion_id || [],
              assignees    : planFor.get(planKey(a.id, a.patientId))?.assignee_id || [],
            })}
          />
        )}

      {picker && (
        <PromotionPicker
          patientId={picker.patientId}
          patientName={picker.patientName}
          appointmentId={picker.appointmentId}
          initial={picker.initial}
          initialAssignees={picker.assignees || []}
          planDate={planDate}
          branchId={branchId}
          onClose={() => setPicker(null)}
          onSaved={onSaved}
        />
      )}
    </section>
  )
}

// One grouped, tab-shared appointment list. Each row: time · patient (+ scaling
// tag / high-value / promo-added marks) · patient-status · doctor · status code ·
// add-promotion action.
//
// confirmMode (the ต้องยืนยัน tab) adds two things the calling list needs: the
// patient-status pill becomes an editable StatusControl, and each row shows the
// patient's next booking after today.
function GroupedQueue({ groups, planFor, soldIndex, promoNames, staffNames, onPlanUpsell, confirmMode = false, onPatientStatus }) {
  if (!groups.length) return <EmptyState variant="plain" title="ไม่มีรายการ" />
  return (
    <div className="divide-y divide-slate-100">
      {groups.map((g) => (
        <div key={g.label}>
          <div className="px-4 py-2 bg-slate-50/70 text-[12px] font-semibold text-slate-600 flex items-center justify-between">
            <span className="truncate">{g.label}</span>
            <span className="text-slate-400 tabular-nums">{g.rows.length}</span>
          </div>
          <ul>
            {g.rows.map((a, i) => {
              const tone = a.patientStatus ? STATUS_TONES[statusTone(a.patientStatus)] : null
              const plan = planFor.get(planKey(a.id, a.patientId))
              const planned = plan?.promotion_id || []
              const planWho = assigneeNames(plan?.assignee_id, staffNames)
              const { won } = plan ? planOutcome(plan, soldIndex) : { won: false }
              return (
                <li key={i} className="px-4 py-2 flex items-center gap-2.5 text-[13px] hover:bg-slate-50/60">
                  <span className="tabular-nums text-slate-500 w-[46px] flex-none">{hm(a.s)}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <PatientLink patientId={a.patientId} className="font-medium truncate">{a.patient || '—'}</PatientLink>
                      {a.scale?.overdue && <ScaleTag status={a.scale} compact />}
                      {a.highValue && <Sparkles size={13} className="text-violet-500 flex-none" title="เคสน่าสนใจ" />}
                    </div>
                    {(a.hn || a.type) && <div className="text-[11.5px] text-slate-400 truncate">{[a.hn, a.type].filter(Boolean).join(' · ')}</div>}
                  </div>

                  {/* นัดถัดไป — its own column, so the dates line up down the list
                      instead of riding under a name of arbitrary length. */}
                  {confirmMode && (
                    <span className="hidden md:flex w-[108px] flex-none items-center gap-1 text-[11.5px]">
                      {a.patientId > 0 && (a.nextAppt ? (
                        <>
                          <CalendarDays size={11} className="text-slate-400 flex-none" />
                          <span className="text-slate-500 truncate" title={`นัดถัดไป ${fmtDateText(a.nextAppt.date, { weekday: true })}`}>
                            {fmtDate(a.nextAppt.date)}
                          </span>
                          {/* A booking made weeks ahead usually has no status yet —
                              show the code only when there really is one. */}
                          {a.nextAppt.status && (
                            <span
                              title={STATUS_TH[a.nextAppt.status] || a.nextAppt.status}
                              className={`flex-none inline-flex items-center justify-center px-1 rounded text-[10px] font-bold ring-1 ${codeCls(a.nextAppt.status)}`}
                            >{a.nextAppt.status}</span>
                          )}
                        </>
                      ) : (
                        // Worth flagging, not hiding: nothing is booked past today, so
                        // this call is the last chance to rebook — and it is also why
                        // setting มีนัดแล้ว would be rejected upstream.
                        <span className="text-amber-600 truncate">ไม่มีนัดถัดไป</span>
                      ))}
                    </span>
                  )}
                  {/* Fixed width, and rendered even when there is no status: the
                      pill's own width varies with the label (มีนัดแล้ว vs
                      Recall เคลียร์ช่องปาก), which was shunting the doctor column
                      around from row to row. */}
                  <span className="hidden sm:flex w-[132px] flex-none items-center">
                    {confirmMode && a.patientId > 0 ? (
                      <StatusControl
                        compact
                        patientId={a.patientId}
                        status={a.patientStatus}
                        onChanged={(next) => onPatientStatus?.(a.patientId, next)}
                      />
                    ) : tone && (
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10.5px] font-medium ring-1 whitespace-nowrap ${tone.pill}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />{a.patientStatus}
                      </span>
                    )}
                  </span>
                  <span title={a.doctor} className="text-[12px] text-slate-500 w-[140px] flex-none truncate hidden md:block">{a.doctor || '—'}</span>
                  <span title={STATUS_TH[a.status] || a.status} className={`flex-none inline-flex items-center justify-center min-w-[26px] px-1 py-0.5 rounded text-[11px] font-bold ring-1 ${codeCls(a.status)}`}>{a.status || '—'}</span>
                  {/* Fixed width so the tag column lines up whether or not a
                      row carries a plan count. */}
                  <span className="flex-none w-[42px] flex justify-end">
                  {a.patientId > 0 && (
                    planned.length > 0
                      // Planned: the tag carries the count, and turns green once
                      // any of the planned promotions actually sold today.
                      ? <button
                          onClick={() => onPlanUpsell(a)}
                          title={[`แผนอัปเซล: ${planned.map((id) => promoNames.get(Number(id)) || `#${id}`).join(', ')}${won ? ' — ปิดได้แล้ว' : ' — ยังไม่ปิด'}`, planWho.length && `ผู้รับผิดชอบ: ${planWho.join(', ')}`].filter(Boolean).join('\n')}
                          className={`inline-flex items-center gap-1 pl-1.5 pr-2 py-1 rounded-md ring-1 ${
                            won ? 'bg-emerald-50 text-emerald-600 ring-emerald-200 hover:bg-emerald-100'
                                : 'bg-brand-50 text-brand-600 ring-brand-200 hover:bg-brand-100'}`}
                        >
                          {won ? <Check size={14} /> : <Tag size={14} />}
                          <span className="text-[11px] font-semibold tabular-nums leading-none">{planned.length}</span>
                        </button>
                      : <button onClick={() => onPlanUpsell(a)} title="วางแผนอัปเซล" className="p-1.5 rounded-md text-slate-400 hover:text-brand-600 hover:bg-brand-50">
                          <Tag size={14} />
                        </button>
                  )}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </div>
  )
}

// เคสน่าสนใจ (auto-flagged high-value) + the day's upsell plans with their outcome.
function UpsaleTab({ notable, plans, soldIndex, promoNames, staffNames, nameByPatient }) {
  // One row per plan (= one appointment), with each planned promotion as a chip.
  // A chip ticks green when that exact promotion was assigned to the patient
  // today; the plan itself counts as won when ANY chip ticked.
  const rows = useMemo(() => plans.map((pl) => {
    const { won, hits } = planOutcome(pl, soldIndex)
    return { plan: pl, won, hits, who: assigneeNames(pl.assignee_id, staffNames) }
  }), [plans, soldIndex, staffNames])
  const wonCount = rows.filter((r) => r.won).length

  return (
    <div>
      <div className="px-4 py-2 bg-slate-50/70 text-[12px] font-semibold text-slate-600 flex items-center gap-2">
        <Sparkles size={14} className="text-violet-500" /> เคสน่าสนใจ <span className="text-slate-400 tabular-nums">{notable.length}</span>
        <span className="text-[11px] text-slate-400 font-normal">— ตรวจจับอัตโนมัติจากชนิดการรักษา</span>
      </div>
      {notable.length === 0 ? (
        <div className="px-4 py-3 text-[12.5px] text-slate-400">ไม่มีเคสน่าสนใจ</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {notable.map((a, i) => (
            <li key={i} className="px-4 py-2 flex items-center gap-3 text-[13px]">
              <span className="tabular-nums text-slate-500 w-[46px] flex-none">{hm(a.s)}</span>
              <span className="flex-1 min-w-0 truncate"><PatientLink patientId={a.patientId} className="font-medium">{a.patient || '—'}</PatientLink></span>
              <span className="text-[12px] text-slate-600 truncate max-w-[240px]">{a.type || '—'}</span>
              <span className="text-[12px] text-slate-400 hidden sm:block">{a.doctor}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="px-4 py-2 bg-slate-50/70 text-[12px] font-semibold text-slate-600 flex items-center gap-2 border-t border-slate-100">
        <Tag size={14} className="text-brand-500" /> แผนอัปเซลวันนี้ <span className="text-slate-400 tabular-nums">{rows.length}</span>
        {rows.length > 0 && (
          <span className="ml-auto text-[11px] font-normal text-slate-400">
            ปิดได้ <span className="text-emerald-600 font-semibold tabular-nums">{wonCount}</span> / {rows.length}
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-3 text-[12.5px] text-slate-400">ยังไม่มีแผน — กดไอคอน <Tag size={12} className="inline align-text-bottom" /> ที่รายการคิวนัดเพื่อวางแผนอัปเซลให้คนไข้</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map(({ plan, won, hits, who }) => (
            <li key={plan.id} className="px-4 py-2 flex items-start gap-3 text-[13px]">
              <span className={`flex-none mt-0.5 ${won ? 'text-emerald-500' : 'text-slate-300'}`} title={won ? 'ปิดการขายได้' : 'ยังไม่ปิด'}>
                <Check size={14} />
              </span>
              <span className="flex-1 min-w-0 pt-0.5">
                <span className="block truncate">
                  <PatientLink patientId={plan.patient_id} className="font-medium">{nameByPatient.get(Number(plan.patient_id)) || `#${plan.patient_id}`}</PatientLink>
                </span>
                {/* Who owns the pitch. A plan with nobody on it is worth seeing
                    as such, so this line renders either way. */}
                <span className={`mt-0.5 flex items-center gap-1 text-[11.5px] truncate ${who.length ? 'text-slate-500' : 'text-slate-300'}`}>
                  <User size={11} className="flex-none" />
                  {who.length ? who.join(' · ') : 'ยังไม่มีผู้รับผิดชอบ'}
                </span>
              </span>
              <span className="flex flex-wrap justify-end gap-1 max-w-[62%]">
                {(plan.promotion_id || []).map((id) => {
                  const hit = hits.has(Number(id))
                  return (
                    <span
                      key={id}
                      title={hit ? 'ขายได้แล้ววันนี้' : 'วางแผนไว้ ยังไม่ได้ขาย'}
                      className={`text-[12px] rounded px-2 py-0.5 truncate max-w-[260px] inline-flex items-center gap-1 ${
                        hit ? 'text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200' : 'text-brand-700 bg-brand-50'}`}
                    >
                      {hit && <Check size={11} className="flex-none" />}
                      {promoNames.get(Number(id)) || `#${id}`}
                    </span>
                  )
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
